/**
 * EBOLT cart behaviour (New Look).
 *
 * 1. Add to cart on product pages and product cards (forms marked data-ebolt-add)
 *    adds the item in place and opens the slide-out cart, instead of leaving for
 *    /cart. It fires the same CartLinesUpdateEvent Horizon's own product form fires,
 *    so the header count, the cart drawer contents and its auto-open all follow.
 *    Forms marked data-cart-redirect="true" (free-gift deals, where the BOGOS app
 *    adds or offers the free item) keep the normal post to the cart page. Without
 *    a cart drawer on the page, or if the request fails, the form posts normally.
 *
 * 2. The cart page (section ebolt-cart, [data-ebolt-cart]) updates totals by itself
 *    when a quantity changes or a line is removed: /cart/update.js with the section
 *    re-rendered in the same response, then a second quiet refresh a moment later in
 *    case an app (BOGOS) changed the cart in response. The "Update quantities"
 *    button stays as the no-JavaScript fallback.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const JSON_HEADERS = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

async function fetchCart() {
  const response = await fetch(`${ROOT}cart.js`, { headers: JSON_HEADERS });
  return response.json();
}

/* ---------- 1. add to cart ---------- */

/** @param {HTMLButtonElement | null} button @param {'adding' | 'added' | 'idle'} state */
function setButton(button, state) {
  if (!button) return;
  if (!button.dataset.label) button.dataset.label = button.textContent.trim();
  clearTimeout(Number(button.dataset.timer || 0));
  button.classList.toggle('is-adding', state === 'adding');
  button.classList.toggle('is-added', state === 'added');
  if (state === 'adding') {
    button.setAttribute('aria-disabled', 'true');
    button.textContent = 'Adding…';
  } else if (state === 'added') {
    button.removeAttribute('aria-disabled');
    button.textContent = 'Added to cart';
    button.dataset.timer = String(setTimeout(() => setButton(button, 'idle'), 2200));
  } else {
    button.removeAttribute('aria-disabled');
    button.textContent = button.dataset.label;
    button.classList.remove('is-adding', 'is-added');
  }
}

/** @param {HTMLFormElement} form @param {string} message */
function showError(form, message) {
  let note = form.querySelector('.ebolt-add-error');
  if (!note) {
    note = document.createElement('p');
    note.className = 'ebolt-add-error';
    note.setAttribute('role', 'alert');
    form.append(note);
  }
  note.textContent = message;
  clearTimeout(Number(form.dataset.errorTimer || 0));
  form.dataset.errorTimer = String(setTimeout(() => note.remove(), 8000));
}

/** @param {SubmitEvent} event */
async function onAddSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-ebolt-add')) return;
  if (form.dataset.cartRedirect === 'true') return;
  if (!document.querySelector('cart-drawer-component')) return;
  if (event.defaultPrevented) return;

  event.preventDefault();
  const button = /** @type {HTMLButtonElement | null} */ (
    event.submitter instanceof HTMLButtonElement ? event.submitter : form.querySelector('button[type="submit"]')
  );
  if (form.dataset.busy === 'true' || button?.getAttribute('aria-disabled') === 'true') return;
  form.dataset.busy = 'true';
  form.querySelector('.ebolt-add-error')?.remove();
  setButton(button, 'adding');

  const formData = new FormData(form);
  const quantity = Number(formData.get('quantity')) || 1;
  // the slide-out cart's new contents come back with the add, so it opens already filled
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(`${ROOT}cart/add.js`, { method: 'POST', body: formData, headers: JSON_HEADERS });
    const result = await response.json();
    const cart = await fetchCart();
    const didError = !response.ok || Boolean(result.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError, items: cart.items, itemCount: quantity, source: 'ebolt-cart', sections: didError ? undefined : result.sections },
    });
    if (didError) {
      setButton(button, 'idle');
      showError(form, result.description || result.message || 'This item could not be added. Please call (217) 793-2940.');
    } else {
      setButton(button, 'added');
      const drawer = cartDrawer();
      if (drawer && !drawer.hasAttribute('open') && typeof drawer.open === 'function') drawer.open();
    }
  } catch (error) {
    deferred.reject(error);
    setButton(button, 'idle');
    delete form.dataset.busy;
    form.submit(); // fall back to the normal add, which opens the cart page
    return;
  }
  delete form.dataset.busy;
}

document.addEventListener('submit', onAddSubmit);

/** The <theme-drawer> holding the slide-out cart, if this page has one. */
function cartDrawer() {
  const el = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
  return /** @type {(HTMLElement & { open?: () => void, close?: () => void }) | null} */ (el || null);
}

// On computers the cart slides over a dimmed page (assets/ebolt-overrides.css paints the dimming on
// body::after); a click on it lands on <body> and closes the cart.
document.addEventListener('click', (event) => {
  if (event.target !== document.body) return;
  const drawer = cartDrawer();
  if (drawer?.hasAttribute('open') && typeof drawer.close === 'function') drawer.close();
});

/* ---------- 2. cart page ---------- */

/** @param {HTMLElement} root */
function initCartPage(root) {
  if (root.dataset.live === 'true') return;
  root.dataset.live = 'true';
  root.classList.add('is-live');
  const sectionId = root.dataset.sectionId || '';
  const status = document.createElement('p');
  status.className = 'visually-hidden';
  status.setAttribute('aria-live', 'polite');
  root.after(status);

  let timer = 0;
  let running = false;
  let again = false;
  let quietTimer = 0;

  const inputs = () => /** @type {HTMLInputElement[]} */ ([...root.querySelectorAll('input[name="updates[]"]')]);

  /** @param {string} html */
  function swap(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.getElementById(root.id);
    if (!fresh) return false;
    const focused = document.activeElement;
    const focusId = focused instanceof HTMLElement && root.contains(focused) ? focused.id : '';
    root.innerHTML = fresh.innerHTML;
    if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
    return true;
  }

  async function quietRefresh() {
    try {
      const response = await fetch(`${ROOT}cart?sections=${encodeURIComponent(sectionId)}`, { headers: JSON_HEADERS });
      const sections = await response.json();
      const html = sections[sectionId];
      if (!html) return;
      const sig = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-sig]')?.getAttribute('data-cart-sig');
      if (sig && sig !== root.querySelector('[data-cart-sig]')?.getAttribute('data-cart-sig')) {
        swap(html);
        const cart = await fetchCart();
        const deferred = CartLinesUpdateEvent.createPromise();
        root.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise }));
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: false, items: cart.items, source: 'ebolt-cart' } });
      }
    } catch (error) {
      /* the page still shows the last good totals */
    }
  }

  async function push() {
    if (running) {
      again = true;
      return;
    }
    running = true;
    const wanted = inputs().map((input) => Math.max(0, parseInt(input.value, 10) || 0));
    const titles = inputs().map((input) => input.closest('.ebolt-line')?.querySelector('.ebolt-line__title')?.textContent?.trim() || 'this item');
    root.classList.add('is-busy');
    root.setAttribute('aria-busy', 'true');
    const deferred = CartLinesUpdateEvent.createPromise();
    root.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: inputs().map((input, i) => ({ merchandiseId: input.dataset.variantId || '', quantity: wanted[i] })),
        promise: deferred.promise,
      })
    );
    try {
      const response = await fetch(`${ROOT}cart/update.js`, {
        method: 'POST',
        headers: { ...JSON_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: wanted, sections: sectionId }),
      });
      const cart = await response.json();
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'update failed');
      const html = cart.sections && cart.sections[sectionId];
      if (!html || !swap(html)) {
        window.location.reload();
        return;
      }
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: false, items: cart.items, source: 'ebolt-cart' } });
      // Shopify lowers a quantity to what is in stock; say so on that line.
      const now = inputs().map((input) => parseInt(input.value, 10) || 0);
      let message = cart.item_count > 0 ? 'Cart updated.' : 'Your cart is empty.';
      if (now.length === wanted.filter((q) => q > 0).length) {
        let j = 0;
        wanted.forEach((q, i) => {
          if (q === 0) return;
          if (now[j] < q) {
            const line = inputs()[j].closest('.ebolt-line');
            const note = document.createElement('p');
            note.className = 'ebolt-line__note';
            note.textContent = `Only ${now[j]} available online. Call (217) 793-2940 for more.`;
            line?.querySelector('.ebolt-line__info')?.append(note);
            message = `Only ${now[j]} of ${titles[i]} available.`;
          }
          j += 1;
        });
      }
      status.textContent = message;
      clearTimeout(quietTimer);
      quietTimer = window.setTimeout(quietRefresh, 1600);
    } catch (error) {
      deferred.reject(error);
      // fall back to the normal form post (Update quantities), which reloads with the right totals
      const form = root.querySelector('form.ebolt-cart__form');
      const update = root.querySelector('.ebolt-cart__update');
      if (form instanceof HTMLFormElement) form.requestSubmit(update instanceof HTMLButtonElement ? update : null);
      return;
    } finally {
      root.classList.remove('is-busy');
      root.removeAttribute('aria-busy');
      running = false;
    }
    if (again) {
      again = false;
      push();
    }
  }

  /** @param {number} delay */
  function schedule(delay) {
    clearTimeout(timer);
    timer = window.setTimeout(push, delay);
  }

  root.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const step = target.closest('[data-qty-step]');
    if (step instanceof HTMLElement) {
      const input = step.parentElement?.querySelector('input');
      if (!(input instanceof HTMLInputElement)) return;
      const next = (parseInt(input.value, 10) || 0) + parseInt(step.dataset.qtyStep || '0', 10);
      input.value = String(Math.max(0, next));
      schedule(450);
      return;
    }
    const remove = target.closest('.ebolt-line__remove');
    if (remove) {
      const input = remove.closest('.ebolt-line')?.querySelector('input[name="updates[]"]');
      if (!(input instanceof HTMLInputElement)) return;
      event.preventDefault();
      input.value = '0';
      const line = remove.closest('.ebolt-line');
      if (line instanceof HTMLElement && !reduceMotion.matches) line.classList.add('is-leaving');
      schedule(0);
    }
  });
  root.addEventListener('input', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'updates[]') schedule(700);
  });
  root.addEventListener('change', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'updates[]') schedule(0);
  });
}

function boot() {
  document.querySelectorAll('[data-ebolt-cart]').forEach((root) => {
    if (root instanceof HTMLElement) initCartPage(root);
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
